import React, { useEffect, useState } from 'react';
import {
  Container,
  Stack,
  Input,
  Textarea,
  Button,
  Alert,
  Grid,
  GridItem,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  CardExpandable,
  Badge,
  Spinner,
  Modal
} from '@/components/ui';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { OrgKnowledgeService } from '@/services/OrgKnowledgeService';
import { 
  FileText, 
  Upload, 
  Trash2, 
  Save, 
  ChevronDown, 
  ExternalLink,
  Plus,
  Globe,
  Rss,
  FileIcon,
  Edit2,
  Search,
  Layers,
  X,
  Eye,
  Database,
  Clock,
  RefreshCw
} from 'lucide-react';

type Source = { id: string; kind: 'manual'|'website'|'rss'|'file'; source_url?: string; title?: string };
type Doc = { 
  id: string; 
  source_id?: string; 
  title: string; 
  content: string; 
  r2Key?: string; 
  publicUrl?: string;
  chunkCount?: number;
};

export function CompanyKnowledge() {
  const svc = new OrgKnowledgeService();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Knowledge Sources removed in Milestone 5
  const [docs, setDocs] = useState<Doc[]>([]);
  const [uploadingDocId, setUploadingDocId] = useState<string | null>(null);
  const [expandedDocs, setExpandedDocs] = useState<Set<string>>(new Set());
  const [editingSource, setEditingSource] = useState<string | null>(null);
  const [editingDoc, setEditingDoc] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<Array<{ id: string; score: number; text: string; docId?: string }>>([]);
  const [viewingChunks, setViewingChunks] = useState<string | null>(null);
  const [chunks, setChunks] = useState<Array<{ id: string; text: string; created_at: string }>>([]);
  const [loadingChunks, setLoadingChunks] = useState(false);
  const [reprocessingDoc, setReprocessingDoc] = useState<string | null>(null);

  const reload = async () => {
    setError(null);
    setLoading(true);
    try {
      const docsData = await svc.listDocs();
      setDocs(docsData);
    } catch (e: any) {
      setError(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  // Simple creators
  const createId = () => `${Date.now()}`;

  // Sources UI removed

  const addDoc = async () => {
    const d: Doc = { id: createId(), title: 'New Document', content: '' };
    setDocs(prev => [...prev, d]);
    setEditingDoc(d.id);
    setExpandedDocs(prev => new Set(prev).add(d.id));
  };
  
  const saveDoc = async (d: Doc) => {
    try {
      await svc.upsertDoc(d);
      await reload();
      setEditingDoc(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to save document');
    }
  };
  
  const removeDoc = async (id: string) => { 
    if (!confirm('Delete this document?')) return;
    try {
      await svc.deleteDoc(id); 
      await reload();
    } catch (e: any) {
      setError(e?.message || 'Failed to delete document');
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    for (const file of Array.from(files)) {
      try {
        const docId = createId();
        setUploadingDocId(docId);
        
        const presigned = await svc.presignDocUpload(file.name, file.type || 'application/octet-stream');
        await svc.uploadToPresignedUrl(presigned.url, file, file.type || 'application/octet-stream');
        await svc.finalizeDocFromR2({ 
          id: presigned.docId, 
          title: file.name, 
          r2Key: presigned.r2Key 
        });
      } catch (e: any) {
        setError(e?.message || 'Upload failed');
      }
    }
    
    setUploadingDocId(null);
    await reload();
  };

  const toggleDocExpanded = (id: string) => {
    setExpandedDocs(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const getSourceIcon = (kind: Source['kind']) => {
    switch (kind) {
      case 'website': return Globe;
      case 'rss': return Rss;
      case 'file': return FileIcon;
      default: return FileText;
    }
  };

  const runSearch = async () => {
    setSearching(true);
    setError(null);
    try {
      const items = await svc.searchKnowledge(search, 8);
      setResults(items);
    } catch (e: any) {
      setError(e?.message || 'Search failed');
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const viewChunks = async (docId: string) => {
    setViewingChunks(docId);
    setLoadingChunks(true);
    try {
      const items = await svc.getDocChunks(docId);
      setChunks(items);
    } catch (e: any) {
      setError(e?.message || 'Failed to load chunks');
      setChunks([]);
    } finally {
      setLoadingChunks(false);
    }
  };

  const reprocessDoc = async (docId: string) => {
    setReprocessingDoc(docId);
    try {
      await svc.reprocessDoc(docId);
      await reload();
    } catch (e: any) {
      setError(e?.message || 'Failed to reprocess document');
    } finally {
      setReprocessingDoc(null);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <Container size="lg">
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="p-8">
        <PageHeader
          title="Company Knowledge"
          description="Capture sources and documents to power AI planning and content generation."
        />
        
        {error && (
          <div className="mb-4">
            <Alert variant="error" title="Error">{error}</Alert>
          </div>
        )}

        {/* Knowledge Sources removed in Milestone 5 */}

        {/* Knowledge Documents Section */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold">Knowledge Documents</h2>
              <p className="text-sm text-muted-foreground mt-1">
                {docs.length} document{docs.length !== 1 ? 's' : ''} • {docs.reduce((sum, d) => sum + (d.chunkCount || 0), 0)} total chunks
              </p>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="secondary" 
                size="sm" 
                disabled={uploadingDocId !== null}
                onClick={() => document.getElementById('file-upload-input')?.click()}
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Files
              </Button>
              <input
                id="file-upload-input"
                type="file"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                accept=".txt,.md,.pdf"
                disabled={uploadingDocId !== null}
              />
              <Button variant="primary" size="sm" onClick={addDoc}>
                <Plus className="w-4 h-4 mr-2" />
                Add Document
              </Button>
            </div>
          </div>

          {/* Search Bar */}
          <Card variant="bordered" className="mb-6">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Search className="w-5 h-5 text-gray-400" />
                <Input 
                  placeholder="Search across all knowledge documents..." 
                  value={search} 
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
                  className="flex-1"
                />
                <Button 
                  variant="primary" 
                  size="sm" 
                  disabled={!search.trim() || searching} 
                  onClick={runSearch}
                >
                  {searching ? <Spinner size="sm" /> : 'Search'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {uploadingDocId && (
            <Alert variant="info" className="mb-4">
              <Spinner size="sm" className="mr-2" />
              Uploading and processing files...
            </Alert>
          )}

          {/* Search Results */}
          {results.length > 0 && (
            <Card variant="elevated" className="mb-6">
              <CardHeader>
                <CardTitle>Search Results</CardTitle>
                <CardDescription>Found {results.length} matches for "{search}"</CardDescription>
              </CardHeader>
              <CardContent>
              <Stack spacing={3}>
                  {results.map(r => {
                    const sourceDoc = docs.find(d => d.id === r.docId);
                    return (
                      <Card key={r.id} variant="bordered">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <div className="text-sm font-medium text-gray-600 mb-1">
                                From: {sourceDoc?.title || 'Unknown Document'}
                              </div>
                              <Badge variant="secondary">
                                Score: {r.score.toFixed(3)}
                              </Badge>
                            </div>
                            {r.docId && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => {
                                  setExpandedDocs(prev => new Set(prev).add(r.docId!));
                                  const el = document.getElementById(`doc-${r.docId}`);
                                  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                View Doc
                              </Button>
                            )}
                          </div>
                          <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700 bg-gray-50 rounded p-3">
                            {r.text}
                          </pre>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Stack>
              </CardContent>
            </Card>
          )}
          
          {/* Document Grid */}
          <Grid cols={1} gap={4}>
            {docs.map((doc) => {
              const isExpanded = expandedDocs.has(doc.id);
              const isEditing = editingDoc === doc.id;
              const hasFile = Boolean(doc.r2Key);
              const isReprocessing = reprocessingDoc === doc.id;
              const hasContent = Boolean(doc.content?.trim());
              
              return (
                <GridItem key={doc.id}>
                  <Card 
                    id={`doc-${doc.id}`} 
                    variant={isEditing ? 'elevated' : 'default'}
                    className="transition-all duration-200"
                  >
                    <CardHeader 
                      className={!isEditing ? "cursor-pointer" : ""}
                      onClick={() => !isEditing && toggleDocExpanded(doc.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1">
                          {!isEditing && (
                            <ChevronDown 
                              className={`w-5 h-5 text-gray-500 transition-transform mt-0.5 ${
                                isExpanded ? 'rotate-180' : ''
                              }`}
                            />
                          )}
                          <div className="flex-1">
                            {isEditing ? (
                              <Input
                                value={doc.title}
                                onChange={(e) => setDocs(prev => 
                                  prev.map(d => d.id === doc.id ? { ...d, title: e.target.value } : d)
                                )}
                                onClick={(e) => e.stopPropagation()}
                                className="text-lg font-semibold mb-2"
                                placeholder="Document title"
                              />
                            ) : (
                              <CardTitle className="text-lg">{doc.title}</CardTitle>
                            )}
                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                              {hasFile && (
                                <Badge variant="secondary">
                                  <FileIcon className="w-3 h-3 mr-1" />
                                  File
                                </Badge>
                              )}
                              {doc.chunkCount !== undefined && doc.chunkCount > 0 && (
                                <Badge variant="default">
                                  <Database className="w-3 h-3 mr-1" />
                                  {doc.chunkCount} chunks
                                </Badge>
                              )}
                              {!hasContent && hasFile && (
                                <Badge variant="warning">
                                  No content extracted
                                </Badge>
                              )}
                            </div>
                          </div>
                    </div>
                        <div className="flex items-center gap-2 ml-2">
                          {doc.publicUrl && !isEditing && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(doc.publicUrl, '_blank');
                              }}
                              title="View original file"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          )}
                    </div>
                  </div>
                    </CardHeader>
                    
                    <CardExpandable isExpanded={isExpanded || isEditing}>
                      <CardContent>
                        {isEditing ? (
                          <Textarea
                            label="Content"
                            rows={8}
                            value={doc.content}
                            onChange={(e) => setDocs(prev => 
                              prev.map(d => d.id === doc.id ? { ...d, content: e.target.value } : d)
                            )}
                            placeholder="Enter document content or upload a file..."
                          />
                        ) : (
                          <div>
                            {hasContent ? (
                              <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700 bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                                {doc.content}
                              </pre>
                            ) : (
                              <div className="text-center py-8 text-gray-500">
                                <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                                <p className="text-sm">No content available</p>
                                {hasFile && (
                                  <Button
                                    variant="secondary"
                                    size="sm"
                                    className="mt-3"
                                    onClick={() => reprocessDoc(doc.id)}
                                    disabled={isReprocessing}
                                  >
                                    {isReprocessing ? (
                                      <Spinner size="sm" className="mr-2" />
                                    ) : (
                                      <RefreshCw className="w-4 h-4 mr-2" />
                                    )}
                                    Reprocess File
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                      
                      <CardFooter className="justify-between border-t pt-3">
                        <div className="flex items-center gap-2">
                          {doc.chunkCount !== undefined && doc.chunkCount > 0 && !isEditing && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                viewChunks(doc.id);
                              }}
                            >
                              <Layers className="w-4 h-4 mr-2" />
                              View Chunks
                            </Button>
                          )}
                          {(!isEditing && (hasFile || hasContent)) && (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); reprocessDoc(doc.id); }}
                              disabled={isReprocessing}
                            >
                              {isReprocessing ? (
                                <Spinner size="sm" className="mr-2" />
                              ) : (
                                <RefreshCw className="w-4 h-4 mr-2" />
                              )}
                              Generate Chunks
                            </Button>
                          )}
                        </div>
                        <div className="flex gap-2">
                          {isEditing ? (
                            <>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => {
                                  setEditingDoc(null);
                                  reload(); // Reset changes
                                }}
                              >
                                Cancel
                              </Button>
                              <Button 
                                variant="primary" 
                                size="sm" 
                                onClick={() => saveDoc(doc)}
                                disabled={!doc.title.trim() || (!doc.content.trim() && !hasFile)}
                              >
                                <Save className="w-4 h-4 mr-2" />
                                Save
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditingDoc(doc.id);
                                  setExpandedDocs(prev => new Set(prev).add(doc.id));
                                }}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeDoc(doc.id);
                                }}
                                className="text-error-600 hover:text-error-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </CardFooter>
                    </CardExpandable>
                  </Card>
                </GridItem>
              );
            })}
            
            {docs.length === 0 && (
              <GridItem className="col-span-full">
                <Card variant="bordered" padding="lg">
                  <CardContent className="text-center text-muted-foreground">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p>No documents yet. Upload files or create your first document.</p>
                  </CardContent>
                </Card>
          </GridItem>
            )}
        </Grid>
        </section>

        {/* Chunks Viewer Modal */}
        <Modal
          isOpen={viewingChunks !== null}
          onClose={() => {
            setViewingChunks(null);
            setChunks([]);
          }}
          title={`Document Chunks - ${docs.find(d => d.id === viewingChunks)?.title || 'Unknown'}`}
          size="lg"
        >
          {loadingChunks ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : (
            <div className="max-h-[70vh] overflow-y-auto">
              {chunks.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Database className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p>No chunks found for this document.</p>
                  <p className="text-sm mt-2">Document may need to be reprocessed.</p>
          </div>
              ) : (
          <Stack spacing={3}>
                  {chunks.map((chunk, idx) => (
                    <Card key={chunk.id} variant="bordered">
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <Badge variant="secondary">
                            Chunk {idx + 1} of {chunks.length}
                          </Badge>
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(chunk.created_at).toLocaleDateString()}
                          </span>
                </div>
                      </CardHeader>
                      <CardContent>
                        <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700 bg-gray-50 rounded p-3">
                          {chunk.text}
                        </pre>
                      </CardContent>
                    </Card>
                  ))}
          </Stack>
              )}
            </div>
          )}
        </Modal>
    </div>
  );
}
